import React, { useState, useEffect, useMemo } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Table, Button, Space, DatePicker, Select, Switch } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  REPORT_DATE_FORMAT,
  parseStoredDateRange,
  serializeDateRange,
  formatReportDate,
} from "../utils/reportDateRangeUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;

const FILTERS_STORAGE_KEY = "report:so-pending-for-bill:filters";

const BILL_COLUMN_KEYS = new Set(["billNumber", "billDate", "billAmount"]);

const resolveSalesOrderId = (row = {}) => {
  if (row.soId) return row.soId;
  if (row.salesOrderId) {
    return typeof row.salesOrderId === "object"
      ? row.salesOrderId._id || row.salesOrderId.id
      : row.salesOrderId;
  }
  if (row._id) return row._id;
  if (row.id) return row.id;
  return null;
};

const SOPendingForBillReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [grandTotals, setGrandTotals] = useState(null);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [showTotalPendingOnly, setShowTotalPendingOnly] = useState(false);

  // Restore filters on mount (Back navigation / refresh).
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      if (typeof parsed.financialYear === "string" && parsed.financialYear) {
        setFinancialYear(parsed.financialYear);
      }
      const restoredDateRange = parseStoredDateRange(parsed.dateRange);
      if (restoredDateRange) setDateRange(restoredDateRange);
      if (typeof parsed.showTotalPendingOnly === "boolean") {
        setShowTotalPendingOnly(parsed.showTotalPendingOnly);
      }
    } catch (e) {
      // Ignore bad JSON / storage issues
    } finally {
      setFiltersHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters whenever they change.
  useEffect(() => {
    if (!filtersHydrated) return;
    try {
      const payload = {
        financialYear,
        dateRange: serializeDateRange(dateRange),
        showTotalPendingOnly,
      };
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch (e) {
      // Ignore quota / storage issues
    }
  }, [filtersHydrated, financialYear, dateRange, showTotalPendingOnly]);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    if (!filtersHydrated) return;

    const isCurrent = beginRequest();
    const fetchSOPendingData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("financialYear", financialYear);
        const startDate = formatReportDate(dateRange?.[0]);
        const endDate = formatReportDate(dateRange?.[1]);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (showTotalPendingOnly) {
          params.set("showTotalPendingOnly", "true");
        }
        const query = `?${params.toString()}`;
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getSOPendingForBillReport${query}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const result = await response.json();
        if (!isCurrent()) return;

        if (result.success) {
          setData(
            (result.data || []).map((row) => ({
              ...row,
              soId: resolveSalesOrderId(row),
            })),
          );
          setGrandTotals(result.grandTotals ?? null);
        } else {
          console.error("Failed to fetch SO Pending data:", result.message);
          setGrandTotals(null);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Error fetching SO Pending data:", error);
        setGrandTotals(null);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchSOPendingData();
  }, [
    apiBaseUrl,
    dateRange,
    financialYear,
    filtersHydrated,
    showTotalPendingOnly,
  ]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isExcel", "true");
      const startDate = formatReportDate(dateRange?.[0]);
      const endDate = formatReportDate(dateRange?.[1]);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (showTotalPendingOnly) {
        params.set("showTotalPendingOnly", "true");
      }
      const query = `?${params.toString()}`;
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getSOPendingForBillReport${query}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const dateLabel =
          startDate && endDate ? `${startDate}_to_${endDate}` : "all_dates";
        a.download = `SO_Pending_For_Bill_Report_${financialYear}_${dateLabel}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const allColumns = useMemo(
    () => [
      {
        title: "SO No.",
        dataIndex: "salesOrderNo",
        key: "salesOrderNo",
        width: 100,
        align: "center",
        render: (v, record) => {
          const soId = record?.soId;
          if (!v || !soId) {
            return (
              <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
                {v || "N/A"}
              </span>
            );
          }

          return (
            <span
              role="link"
              tabIndex={0}
              style={{
                color: "#1677ff",
                fontSize: "12px",
                fontWeight: "bold",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => navigate(`/sales-order/${soId}?tab=1`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/sales-order/${soId}?tab=1`);
                }
              }}
              title="Open Sales Order"
            >
              {v}
            </span>
          );
        },
      },
      {
        title: "Order Date",
        dataIndex: "orderDate",
        key: "orderDate",
        width: 100,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "N/A"}</span>
        ),
      },
      {
        title: "Party Name",
        dataIndex: "partyName",
        key: "partyName",
        width: 200,
        align: "left",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "N/A"}</span>
        ),
      },
      {
        title: "Brand",
        dataIndex: "brandName",
        key: "brandName",
        width: 120,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "N/A"}</span>
        ),
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        width: 100,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
        ),
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",
        width: 100,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
        ),
      },
      {
        title: "Salesman",
        dataIndex: "salesman",
        key: "salesman",
        width: 150,
        align: "left",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "N/A"}</span>
        ),
      },
      {
        title: "Order Amount",
        dataIndex: "orderAmount",
        key: "orderAmount",
        width: 120,
        align: "right",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
            {v != null ? formatCurrency(v) : "₹0.00"}
          </span>
        ),
      },
      {
        title: "Pending Amount",
        dataIndex: "pendingAmount",
        key: "pendingAmount",
        width: 120,
        align: "right",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
            {v != null ? formatCurrency(v) : "₹0.00"}
          </span>
        ),
      },
      {
        title: "Bill No.",
        dataIndex: "billNumber",
        key: "billNumber",
        width: 100,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
        ),
      },
      {
        title: "Bill Date",
        dataIndex: "billDate",
        key: "billDate",
        width: 100,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "N/A"}</span>
        ),
      },
      {
        title: "Bill Amount",
        dataIndex: "billAmount",
        key: "billAmount",
        width: 120,
        align: "right",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>
            {v != null ? formatCurrency(v) : "₹0.00"}
          </span>
        ),
      },
      {
        title: "HO",
        dataIndex: "suffix",
        key: "suffix",
        width: 80,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
        ),
      },
    ],
    [navigate],
  );

  const columns = useMemo(
    () =>
      showTotalPendingOnly
        ? allColumns.filter((col) => !BILL_COLUMN_KEYS.has(col.key))
        : allColumns,
    [allColumns, showTotalPendingOnly],
  );

  const summaryColSpanBeforeAmounts = useMemo(() => {
    const orderAmountIndex = columns.findIndex((c) => c.key === "orderAmount");
    return orderAmountIndex > 0 ? orderAmountIndex : 7;
  }, [columns]);

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "12px",
            fontWeight: "bold",
            padding: "8px 4px",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              SO Pending For Bill Report
            </Space>
            <Space wrap>
              <Space size={8}>
                <span style={{ fontSize: 13 }}>Show Total Pending Only</span>
                <Switch
                  checked={showTotalPendingOnly}
                  onChange={setShowTotalPendingOnly}
                />
              </Space>
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format={REPORT_DATE_FORMAT}
                style={{ width: 220 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
                style={{ marginLeft: "8px" }}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          components={components}
          rowKey={(record, index) =>
            record.key || `${record.salesOrderNo}-${record.suffix || ""}-${index}`
          }
          pagination={false}
          scroll={{ x: showTotalPendingOnly ? 1100 : 1400, y: 600 }}
          size="small"
          summary={() =>
            grandTotals ? (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell
                    index={0}
                    colSpan={summaryColSpanBeforeAmounts}
                    align="center"
                  >
                    <strong>Grand Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={summaryColSpanBeforeAmounts} align="right">
                    <strong>{formatCurrency(grandTotals.orderAmount ?? 0)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={summaryColSpanBeforeAmounts + 1}
                    align="right"
                  >
                    <strong>
                      {formatCurrency(grandTotals.pendingAmount ?? 0)}
                    </strong>
                  </Table.Summary.Cell>
                  {!showTotalPendingOnly && (
                    <>
                      <Table.Summary.Cell
                        index={summaryColSpanBeforeAmounts + 2}
                        align="center"
                      />
                      <Table.Summary.Cell
                        index={summaryColSpanBeforeAmounts + 3}
                        align="center"
                      />
                      <Table.Summary.Cell
                        index={summaryColSpanBeforeAmounts + 4}
                        align="right"
                      >
                        <strong>
                          {formatCurrency(grandTotals.billAmount ?? 0)}
                        </strong>
                      </Table.Summary.Cell>
                    </>
                  )}
                  <Table.Summary.Cell
                    index={
                      showTotalPendingOnly
                        ? summaryColSpanBeforeAmounts + 2
                        : summaryColSpanBeforeAmounts + 5
                    }
                    align="center"
                  />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </Card>
    </div>
  );
};

export default SOPendingForBillReport;
